// Quota Service for tracking daily Veo 3 API usage

interface QuotaData {
  date: string;
  used: number;
  total: number;
  remaining?: number;
}

export class QuotaService {
  private static readonly QUOTA_KEY = 'veo3_daily_quota';
  private static readonly DAILY_LIMIT = 3; // 3 free videos per day
  private static readonly API_URL = '/api/quota'; // Vercel API route

  /**
   * Get current quota status (tries server first, falls back to localStorage)
   */
  static async getQuota(): Promise<QuotaData> {
    try {
      // Try to get quota from server
      const response = await fetch(`${this.API_URL}?userId=default`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const serverQuota = await response.json();
        // Update local storage with server data
        localStorage.setItem(this.QUOTA_KEY, JSON.stringify({
          date: serverQuota.date,
          used: serverQuota.used,
          total: serverQuota.total,
        }));
        return serverQuota;
      }
    } catch (error) {
      console.warn('Could not fetch quota from server, using local storage:', error);
    }

    // Fallback to localStorage
    return this.getLocalQuota();
  }

  /**
   * Get quota from localStorage (fallback)
   */
  private static getLocalQuota(): QuotaData {
    const today = new Date().toISOString().split('T')[0];
    const savedData = localStorage.getItem(this.QUOTA_KEY);
    
    if (savedData) {
      const quota: QuotaData = JSON.parse(savedData);
      
      // Reset quota if it's a new day
      if (quota.date !== today) {
        return this.resetQuota();
      }
      
      return quota;
    }
    
    // Initialize quota for first time
    return this.resetQuota();
  }

  /**
   * Use one quota
   */
  static async useQuota(): Promise<boolean> {
    const quota = await this.getQuota();
    
    if (quota.used >= quota.total) {
      return false; // No quota remaining
    }
    
    // Try to update server first
    try {
      const response = await fetch(`${this.API_URL}?userId=default`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const serverQuota = await response.json();
        // Update local storage with server data
        localStorage.setItem(this.QUOTA_KEY, JSON.stringify({
          date: serverQuota.date,
          used: serverQuota.used,
          total: serverQuota.total,
        }));
        return true;
      }
    } catch (error) {
      console.warn('Could not update quota on server, using local storage:', error);
    }
    
    // Fallback to local storage
    quota.used += 1;
    this.saveQuota(quota);
    return true;
  }

  /**
   * Check if quota is available
   */
  static async hasQuota(): Promise<boolean> {
    const quota = await this.getQuota();
    return quota.used < quota.total;
  }

  /**
   * Get remaining quota count
   */
  static async getRemainingQuota(): Promise<number> {
    const quota = await this.getQuota();
    return Math.max(0, quota.total - quota.used);
  }

  /**
   * Reset daily quota
   */
  private static resetQuota(): QuotaData {
    const today = new Date().toISOString().split('T')[0];
    const quota: QuotaData = {
      date: today,
      used: 0,
      total: this.DAILY_LIMIT
    };
    this.saveQuota(quota);
    return quota;
  }

  /**
   * Save quota to localStorage
   */
  private static saveQuota(quota: QuotaData): void {
    localStorage.setItem(this.QUOTA_KEY, JSON.stringify(quota));
  }

  /**
   * Force reset quota (for testing)
   */
  static forceReset(): void {
    this.resetQuota();
  }
}